import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiTrash2, FiSave, FiX, FiUser, FiFileText, FiCalendar, FiCreditCard, FiCheck, FiArrowLeft } from 'react-icons/fi';
import { format, addDays } from 'date-fns';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { v4 as uuidv4 } from 'uuid';
import { convertQuoteToInvoice, validateQuoteForConversion } from '../../utils/quoteToInvoiceConverter';
import { Quote, Invoice, Client } from '../../types';
import { useCurrency } from '../../contexts/CurrencyContext';
import styles from './InvoiceCreateFromQuote.module.css';

interface InvoiceForm {
  clientId: string;
  issueDate: Date;
  dueDate: Date;
  notes: string;
  terms: string;
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  taxRate: number;
  taxAmount: number;
}

// Mock clients data (same as in QuoteList)
const mockClients: Client[] = [
  { 
    id: '1', 
    name: 'TechCorp Solutions', 
    email: 'contact@techcorp.com',
    phone: '+1-555-0123',
    paymentTerms: 'net30',
    tags: ['technology', 'enterprise'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-15')
  },
  { 
    id: '2', 
    name: 'Global Enterprises', 
    email: 'procurement@global.com',
    phone: '+1-555-0456',
    paymentTerms: 'net45',
    tags: ['consulting', 'global'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-10')
  },
  { 
    id: '3', 
    name: 'Startup Innovations', 
    email: 'hello@startup.com',
    phone: '+1-555-0789',
    paymentTerms: 'net30',
    tags: ['startup', 'mobile'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-20')
  },
  { 
    id: '4', 
    name: 'Retail Solutions Ltd', 
    email: 'info@retail.com',
    phone: '+1-555-0321',
    paymentTerms: 'net15',
    tags: ['retail', 'ecommerce'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-25')
  },
  { 
    id: '5', 
    name: 'Healthcare Systems', 
    email: 'contact@healthcare.com',
    phone: '+1-555-0654',
    paymentTerms: 'net60',
    tags: ['healthcare', 'enterprise'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-30')
  },
  { 
    id: '6', 
    name: 'HutsTech', 
    email: 'segodi@hutstech.co.za',
    phone: '+27 82 826 8596',
    paymentTerms: 'net30',
    tags: ['tech', 'south_africa'],
    status: 'active',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-02-01')
  }
];

// Mock quotes data (same as in QuoteList)
const mockQuotes: Quote[] = [
  {
    id: '1',
    quoteNumber: 'Q-2024-001',
    clientId: '1',
    client: mockClients[0],
    items: [
      { id: '1', description: 'Web Development Services', quantity: 1, unitPrice: 5000, taxRate: 10, taxAmount: 500, total: 5500 },
      { id: '2', description: 'UI/UX Design', quantity: 1, unitPrice: 2500, taxRate: 10, taxAmount: 250, total: 2750 }
    ],
    subtotal: 7500,
    taxAmount: 750,
    discountAmount: 0,
    total: 8250,
    status: 'sent',
    issueDate: new Date('2024-01-15'),
    validUntil: new Date('2024-02-15'),
    notes: 'Premium web development package with modern design',
    createdBy: 'John Doe',
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15')
  },
  {
    id: '2',
    quoteNumber: 'Q-2024-002',
    clientId: '2',
    client: mockClients[1],
    items: [
      { id: '3', description: 'Consulting Services', quantity: 40, unitPrice: 150, taxRate: 10, taxAmount: 600, total: 6600 },
      { id: '4', description: 'Project Management', quantity: 1, unitPrice: 3000, taxRate: 10, taxAmount: 300, total: 3300 }
    ],
    subtotal: 9000,
    taxAmount: 900,
    discountAmount: 500,
    total: 9400,
    status: 'accepted',
    issueDate: new Date('2024-01-10'),
    validUntil: new Date('2024-02-10'),
    acceptedDate: new Date('2024-01-20'),
    notes: 'Strategic consulting and project management services',
    createdBy: 'Jane Smith',
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: '3',
    quoteNumber: 'Q-2024-003',
    clientId: '3',
    client: mockClients[2],
    items: [
      { id: '5', description: 'Mobile App Development', quantity: 1, unitPrice: 8000, taxRate: 10, taxAmount: 800, total: 8800 },
      { id: '6', description: 'Backend API Development', quantity: 1, unitPrice: 4000, taxRate: 10, taxAmount: 400, total: 4400 }
    ],
    subtotal: 12000,
    taxAmount: 1200,
    discountAmount: 0,
    total: 13200,
    status: 'draft',
    issueDate: new Date('2024-01-20'),
    validUntil: new Date('2024-02-20'),
    notes: 'Complete mobile app solution with backend services',
    createdBy: 'Mike Johnson',
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20')
  },
  {
    id: '4',
    quoteNumber: 'Q-2024-004',
    clientId: '4',
    client: mockClients[3],
    items: [
      { id: '7', description: 'E-commerce Platform', quantity: 1, unitPrice: 6000, taxRate: 10, taxAmount: 600, total: 6600 },
      { id: '8', description: 'Payment Integration', quantity: 1, unitPrice: 2000, taxRate: 10, taxAmount: 200, total: 2200 }
    ],
    subtotal: 8000,
    taxAmount: 800,
    discountAmount: 1000,
    total: 7800,
    status: 'accepted',
    issueDate: new Date('2024-01-25'),
    validUntil: new Date('2024-02-25'),
    acceptedDate: new Date('2024-02-01'),
    notes: 'Complete e-commerce solution with payment processing',
    createdBy: 'Sarah Wilson',
    createdAt: new Date('2024-01-25'),
    updatedAt: new Date('2024-02-01')
  },
  {
    id: '5',
    quoteNumber: 'Q-2024-005',
    clientId: '5',
    client: mockClients[4],
    items: [
      { id: '9', description: 'Healthcare Management System', quantity: 1, unitPrice: 15000, taxRate: 10, taxAmount: 1500, total: 16500 },
      { id: '10', description: 'Training & Support', quantity: 20, unitPrice: 200, taxRate: 10, taxAmount: 400, total: 4400 }
    ],
    subtotal: 19000,
    taxAmount: 1900,
    discountAmount: 0,
    total: 20900,
    status: 'sent',
    issueDate: new Date('2024-01-30'),
    validUntil: new Date('2024-02-28'),
    notes: 'Comprehensive healthcare management solution',
    createdBy: 'David Brown',
    createdAt: new Date('2024-01-30'),
    updatedAt: new Date('2024-01-30')
  },
  {
    id: '6',
    quoteNumber: 'Q-2024-006',
    clientId: '6',
    client: mockClients[5],
    items: [
      { id: '11', description: 'Basic Website', quantity: 1, unitPrice: 1499, taxRate: 15, taxAmount: 224.85, total: 1723.85 },
      { id: '12', description: 'Domain .co.za', quantity: 1, unitPrice: 129, taxRate: 15, taxAmount: 19.35, total: 148.35 },
      { id: '13', description: 'Hosting', quantity: 12, unitPrice: 439, taxRate: 15, taxAmount: 790.20, total: 6058.20 }
    ],
    subtotal: 6067,
    taxAmount: 1034.40,
    discountAmount: 0,
    total: 7101.40,
    status: 'draft',
    issueDate: new Date('2024-02-01'),
    validUntil: new Date('2024-03-01'),
    notes: 'Website development package for HutsTech with domain and hosting',
    createdBy: 'Admin User',
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01')
  }
];

export function InvoiceCreateFromQuote() {
  const navigate = useNavigate();
  const { formatCurrency: formatCurrencyFromContext } = useCurrency();
  const [step, setStep] = useState<'client' | 'quote' | 'details'>('client');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [clientQuotes, setClientQuotes] = useState<Quote[]>([]);
  const [issueDate, setIssueDate] = useState<Date>(new Date());
  const [dueDate, setDueDate] = useState<Date>(addDays(new Date(), 30));
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter quotes for selected client
  useEffect(() => {
    if (selectedClient) {
      const quotes = mockQuotes.filter(quote => quote.clientId === selectedClient.id);
      setClientQuotes(quotes);
    } else {
      setClientQuotes([]);
    }
  }, [selectedClient]);

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setStep('quote');
  };

  const handleQuoteSelect = (quote: Quote) => {
    // Validate quote for conversion
    const validation = validateQuoteForConversion(quote);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    setSelectedQuote(quote);
    setNotes(quote.notes || '');
    setTerms(quote.terms || '');
    setStep('details');
  };

  const handleCreateInvoice = async () => {
    if (!selectedQuote) return;

    setIsSubmitting(true);
    try {
      // Convert quote to invoice
      const invoice = convertQuoteToInvoice({
        quote: selectedQuote,
        dueDate,
        notes,
        terms
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Invoice created from quote:', invoice);
      toast.success('Invoice created successfully from quote!');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to create invoice from quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (step === 'quote') {
      setStep('client');
      setSelectedClient(null);
      setClientQuotes([]);
    } else if (step === 'details') {
      setStep('quote');
      setSelectedQuote(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { label: 'Draft', className: styles.statusDraft },
      sent: { label: 'Sent', className: styles.statusSent },
      accepted: { label: 'Accepted', className: styles.statusAccepted },
      rejected: { label: 'Rejected', className: styles.statusRejected },
      expired: { label: 'Expired', className: styles.statusExpired }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    
    return (
      <span className={`${styles.statusBadge} ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = formatCurrencyFromContext;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button onClick={() => navigate('/invoices')} className={styles.backButton}>
            <FiArrowLeft size={16} />
            Back to Invoices
          </button>
          <div>
            <h1 className={styles.title}>Create Invoice from Quote</h1>
            <p className={styles.subtitle}>
              {step === 'client' && 'Select a client to see their quotes'}
              {step === 'quote' && `Select a quote from ${selectedClient?.name}`}
              {step === 'details' && `Create invoice from ${selectedQuote?.quoteNumber}`}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className={styles.progressSteps}>
        <div className={`${styles.step} ${step === 'client' ? styles.active : ''} ${step === 'quote' || step === 'details' ? styles.completed : ''}`}>
          <div className={styles.stepNumber}>1</div>
          <div className={styles.stepLabel}>Select Client</div>
        </div>
        <div className={`${styles.step} ${step === 'quote' ? styles.active : ''} ${step === 'details' ? styles.completed : ''}`}>
          <div className={styles.stepNumber}>2</div>
          <div className={styles.stepLabel}>Choose Quote</div>
        </div>
        <div className={`${styles.step} ${step === 'details' ? styles.active : ''}`}>
          <div className={styles.stepNumber}>3</div>
          <div className={styles.stepLabel}>Invoice Details</div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Client Selection */}
        {step === 'client' && (
          <motion.div
            key="client"
            className={styles.stepContent}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.clientGrid}>
              {mockClients.map(client => (
                <div
                  key={client.id}
                  className={styles.clientCard}
                  onClick={() => handleClientSelect(client)}
                >
                  <div className={styles.clientAvatar}>
                    <FiUser size={24} />
                  </div>
                  <div className={styles.clientInfo}>
                    <h3 className={styles.clientName}>{client.name}</h3>
                    <p className={styles.clientEmail}>{client.email}</p>
                    <p className={styles.clientPhone}>{client.phone}</p>
                    <div className={styles.clientTags}>
                      {client.tags.map(tag => (
                        <span key={tag} className={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.clientQuotesCount}>
                    {mockQuotes.filter(q => q.clientId === client.id).length} quotes
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Step 2: Quote Selection */}
        {step === 'quote' && (
          <motion.div
            key="quote"
            className={styles.stepContent}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.quoteHeader}>
              <button onClick={handleBack} className={styles.backStepButton}>
                <FiArrowLeft size={16} />
                Back to Client Selection
              </button>
              <h2>Quotes for {selectedClient?.name}</h2>
            </div>

            {clientQuotes.length === 0 ? (
              <div className={styles.noQuotes}>
                <FiFileText size={48} />
                <h3>No quotes found</h3>
                <p>This client doesn't have any quotes yet.</p>
                <button onClick={handleBack} className={styles.backButton}>
                  Choose a different client
                </button>
              </div>
            ) : (
              <div className={styles.quoteGrid}>
                {clientQuotes.map(quote => (
                  <div
                    key={quote.id}
                    className={`${styles.quoteCard} ${quote.status === 'accepted' ? styles.accepted : ''}`}
                    onClick={() => handleQuoteSelect(quote)}
                  >
                    <div className={styles.quoteHeader}>
                      <h3 className={styles.quoteNumber}>{quote.quoteNumber}</h3>
                      {getStatusBadge(quote.status)}
                    </div>
                    
                    <div className={styles.quoteDetails}>
                      <div className={styles.quoteItem}>
                        <FiCalendar size={16} />
                        <span>Issued: {format(quote.issueDate, 'MMM dd, yyyy')}</span>
                      </div>
                      <div className={styles.quoteItem}>
                        <FiCalendar size={16} />
                        <span>Valid until: {format(quote.validUntil, 'MMM dd, yyyy')}</span>
                      </div>
                      <div className={styles.quoteItem}>
                        <FiCreditCard size={16} />
                        <span>Total: {formatCurrency(quote.total)}</span>
                      </div>
                    </div>

                    {quote.notes && (
                      <div className={styles.quoteNotes}>
                        <p>{quote.notes}</p>
                      </div>
                    )}

                    <div className={styles.quoteItems}>
                      <h4>Items:</h4>
                      <ul>
                        {quote.items.map(item => (
                          <li key={item.id}>
                            {item.description} - {item.quantity} × {formatCurrency(item.unitPrice)}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className={styles.quoteActions}>
                      <button className={styles.selectQuoteButton}>
                        <FiCheck size={16} />
                        Select Quote
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Step 3: Invoice Details */}
        {step === 'details' && (
          <motion.div
            key="details"
            className={styles.stepContent}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.quoteHeader}>
              <button onClick={handleBack} className={styles.backStepButton}>
                <FiArrowLeft size={16} />
                Back to Quote Selection
              </button>
              <h2>Create Invoice from {selectedQuote?.quoteNumber}</h2>
            </div>

            <div className={styles.invoiceForm}>
              <div className={styles.formGrid}>
                {/* Quote Summary */}
                <div className={styles.quoteSummary}>
                  <h3>Quote Summary</h3>
                  <div className={styles.summaryItem}>
                    <span>Client:</span>
                    <span>{selectedQuote?.client.name}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span>Quote Total:</span>
                    <span>{formatCurrency(selectedQuote?.total || 0)}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span>Items:</span>
                    <span>{selectedQuote?.items.length}</span>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className={styles.invoiceDetails}>
                  <h3>Invoice Details</h3>
                  
                  <div className={styles.formGroup}>
                    <label>Issue Date *</label>
                    <DatePicker
                      selected={issueDate}
                      onChange={(date: Date) => setIssueDate(date)}
                      className={styles.datePicker}
                      dateFormat="MMM dd, yyyy"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Due Date *</label>
                    <DatePicker
                      selected={dueDate}
                      onChange={(date: Date) => setDueDate(date)}
                      className={styles.datePicker}
                      dateFormat="MMM dd, yyyy"
                      minDate={issueDate}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Notes</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Additional notes for the invoice..."
                      className={styles.textarea}
                      rows={3}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Terms & Conditions</label>
                    <textarea
                      value={terms}
                      onChange={(e) => setTerms(e.target.value)}
                      placeholder="Payment terms and conditions..."
                      className={styles.textarea}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className={styles.formActions}>
                <button
                  type="button"
                  onClick={handleBack}
                  className={styles.cancelButton}
                >
                  Back
                </button>
                <button
                  onClick={handleCreateInvoice}
                  disabled={isSubmitting}
                  className={`${styles.submitButton} ${isSubmitting ? styles.loading : ''}`}
                >
                  <FiSave size={16} />
                  {isSubmitting ? 'Creating Invoice...' : 'Create Invoice'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
